import { AnnexDocument } from "./useAnnexActions";
import {
  addAnnexAvailableDocument,
  addAnnexSection,
  updateAnnexDocument,
  removeAnnexDocument,
  findPredefinedSection,
} from "../Helpers/annexActions";
import { useCallback } from "react";
import { Document } from "../Models/company";
import { PageRange } from "../Models/evaluation";
import { PredefinedSectionDocument } from "../Models/appendixStructure";

export function usePredefinedSectionActions(
  setPredefinedSections: React.Dispatch<
    React.SetStateAction<Record<number, AnnexDocument[]>>
  >,
  predefinedSections: PredefinedSectionDocument[],
  selectedPredefinedSection?: number | null
) {
  const removePredefinedDocument = useCallback(
    (docId: number) => {
      if (!selectedPredefinedSection) return;

      setPredefinedSections((prev) => {
        const selectedPredefinedAnnex = prev[selectedPredefinedSection] || [];

        const updatedAnnex = removeAnnexDocument(
          selectedPredefinedAnnex,
          docId
        );

        return {
          ...prev,
          [selectedPredefinedSection]: updatedAnnex,
        };
      });
    },
    [setPredefinedSections, selectedPredefinedSection]
  );

  const updatePredefinedDocument = useCallback(
    (
      docId: number,
      updates: {
        name?: string;
        pageRanges?: PageRange[] | null;
      }
    ) => {
      if (!selectedPredefinedSection) return;

      setPredefinedSections((prev) => {
        const selectedPredefinedAnnex = prev[selectedPredefinedSection] || [];

        const updatedAnnex = updateAnnexDocument(
          selectedPredefinedAnnex,
          docId,
          updates
        );

        return {
          ...prev,
          [selectedPredefinedSection]: updatedAnnex,
        };
      });
    },
    [setPredefinedSections, selectedPredefinedSection]
  );

  const replacePredefinedSection = useCallback(
    (
      sectionId: string,
      options?: {
        docId?: number;
        availableDoc?: Document;
        type?: "title" | "empty_section";
      }
    ) => {
      if (!selectedPredefinedSection) return;

      setPredefinedSections((prev) => {
        const selectedPredefinedAnnex = prev[selectedPredefinedSection] || [];
        const section = findPredefinedSection(predefinedSections, sectionId);

        if (!section || !section.order) return prev;

        if (options?.docId) {
          const updatedAnnex = updateAnnexDocument(
            selectedPredefinedAnnex,
            options.docId,
            {
              name: section.name,
              order: section.order,
              placeholder_id: section.id,
            }
          );

          return {
            ...prev,
            [selectedPredefinedSection]: updatedAnnex,
          };
        }

        if (options?.availableDoc) {
          const result = addAnnexAvailableDocument(
            selectedPredefinedAnnex,
            options.availableDoc
          );

          const updatedAnnex = updateAnnexDocument(
            result,
            options.availableDoc.id,
            {
              name: section.name,
              order: section.order,
              placeholder_id: section.id,
            }
          );

          return {
            ...prev,
            [selectedPredefinedSection]: updatedAnnex,
          };
        }

        const updatedAnnex = addAnnexSection(
          selectedPredefinedAnnex,
          options?.type || "title",
          {
            title: section.name,
            placeholder_id: section.id,
            order: section.order,
          }
        );

        return {
          ...prev,
          [selectedPredefinedSection]: updatedAnnex,
        };
      });
    },
    [setPredefinedSections, predefinedSections, selectedPredefinedSection]
  );

  const addAvailablePredefinedDocument = useCallback(
    (doc: Document) => {
      if (!selectedPredefinedSection) return;

      setPredefinedSections((prev) => {
        const annex = prev[selectedPredefinedSection] || [];
        const placeholders = annex.map((section) => section.placeholder_id);

        const flatPredefinedSections = predefinedSections.flatMap((doc) => [
          doc,
          ...(doc.subsections || []),
        ]);

        for (const section of flatPredefinedSections) {
          if (placeholders.includes(section.id)) continue;

          const result = addAnnexAvailableDocument(annex, doc);
          const updatedAnnex = updateAnnexDocument(result, doc.id, {
            name: section.name,
            order: section.order,
            placeholder_id: section.id,
          });

          return {
            ...prev,
            [selectedPredefinedSection]: updatedAnnex,
          };
        }

        return prev;
      });
    },
    [setPredefinedSections, selectedPredefinedSection, predefinedSections]
  );

  return {
    replacePredefinedSection,
    removePredefinedDocument,
    updatePredefinedDocument,
    addAvailablePredefinedDocument,
  };
}
